.h { margin: 30px 0 12px; font: 400 21px/1.25 var(--serif); color: var(--ink); }

/* No max-width: inside the reader the column is already the measure, and a
   second, different cap here is exactly what produced two right edges. */
.list { margin: 0 0 18px; padding-left: 22px; max-width: var(--measure); font: 400 16.5px/1.7 var(--sans); color: var(--body); }
.list li { margin-bottom: 7px; }
.list strong { font-weight: 700; color: var(--ink); }
.list em { font-style: italic; }

.tableWrap { overflow-x: auto; margin: 22px 0; }
.table { width: 100%; border-collapse: collapse; }
.table th {
  font: 500 10px var(--mono); letter-spacing: .2em; text-transform: uppercase;
  color: var(--olive); text-align: left; padding: 10px 12px;
  border-bottom: 2px solid var(--rule-strong);
}
/* Code in a header keeps its case: `i` and `I` are different names. */
.table th code { text-transform: none; letter-spacing: normal; }
.table td {
  padding: 11px 12px; border-bottom: 1px solid var(--rule);
  font: 400 15px/1.6 var(--sans); color: var(--body);
}
.table strong { font-weight: 700; color: var(--ink); }

.figure {
  margin: 26px 0 0; display: flex; align-items: center; justify-content: center;
  border: 1px solid var(--ink-16); border-radius: var(--radius-lg);
  background: repeating-linear-gradient(135deg, var(--panel) 0 3px, var(--panel-2) 3px 11px);
  font: 400 10.5px var(--mono); letter-spacing: .14em; color: var(--ink-55);
}

/* Track content — no tabs. The language was chosen once; this is just content
   in that language, styled no differently from the prose around it. */
.track { margin: 0; }
.trackFallback {
  margin: 0 0 12px; padding: 9px 13px;
  border-left: 2px solid var(--gold-rule); background: var(--panel-2);
  font: 400 12.5px/1.5 var(--sans); color: var(--ink-68);
}

/* Video ------------------------------------------------------------------ */
.video { margin: 26px 0; }
.videoFrame {
  position: relative; aspect-ratio: 16 / 9; width: 100%;
  border-radius: var(--radius-lg); overflow: hidden;
  background: repeating-linear-gradient(135deg, #1a1a1a 0 4px, #232323 4px 14px);
  display: flex; align-items: center; justify-content: center;
}
.videoFrame iframe, .videoFrame video { position: absolute; inset: 0; width: 100%; height: 100%; border: 0; }
.playMark {
  width: 68px; height: 68px; border-radius: 50%;
  border: 1px solid rgba(255, 214, 51, .5); background: rgba(26, 26, 26, .65);
  color: #ffd633; font: 400 18px var(--sans);
  display: flex; align-items: center; justify-content: center;
}
.videoStub {
  position: absolute; left: 18px; bottom: 14px;
  font: 400 9.5px var(--mono); letter-spacing: .14em; color: rgba(244, 244, 242, .34);
}
.videoMeta {
  display: flex; flex-wrap: wrap; gap: 10px; align-items: baseline;
  margin-top: 10px; font: 400 12.5px var(--sans); color: var(--ink-55);
}
.videoTitle { font: 500 13px var(--sans); color: var(--ink); }

/* MDN reference ---------------------------------------------------------- */
.mdn {
  margin: 26px 0; padding: 16px 20px 14px;
  border: 1px solid var(--rule); border-radius: var(--radius-lg);
  background: var(--panel-2);
}
.mdnList { margin: 10px 0 0; padding: 0; list-style: none; }
.mdnItem { padding: 7px 0; border-top: 1px solid var(--ink-08); }
.mdnItem:first-child { border-top: none; }
.mdnLink { font: 500 13.5px var(--sans); color: var(--olive); text-decoration: none; }
.mdnLink:hover { text-decoration: underline; }
.mdnLink::after { content: ' ↗'; font-size: .85em; opacity: .6; }
.mdnNote { margin: 2px 0 0; font: 400 12.5px/1.55 var(--sans); color: var(--ink-55); }

/* Beats ------------------------------------------------------------------
   Seven beats, deliberately NOT seven cards. A page that alternates filled
   panels reads as a stack of notices and the eye stops distinguishing them —
   so only the two that interrupt you are panels, and the rest carry their
   weight with rules, spacing and type. */
.beat { margin: 30px 0; }
.beatHead {
  display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap;
  font: 600 10.5px var(--mono); letter-spacing: .16em; text-transform: uppercase;
}
.beatLabel { font: 400 11.5px var(--sans); letter-spacing: 0; text-transform: none; opacity: .75; }
.beatBody { margin-top: 12px; }
.beatBody > :last-child { margin-bottom: 0; }

/* 1 · hook — a panel, because it interrupts: do this before reading on */
.hook {
  padding: 22px 24px;
  background: var(--try-bg);
  border-left: 3px solid var(--accent);
  border-radius: 0 var(--radius-lg) var(--radius-lg) 0;
}
.hook .beatHead { color: var(--olive-2); }

/* the answer and the wrong turns — prose with a rule, not a panel */
.anticipated { padding-left: 20px; border-left: 2px solid var(--gold-rule); }
.anticipated .beatHead { color: var(--olive); }
.anticipated strong { color: var(--ink); }

/* what you'll be able to do — compact, between hairlines */
.outcomes {
  padding: 16px 0;
  border-top: 1px solid var(--rule);
  border-bottom: 1px solid var(--rule);
}
.outcomes .beatHead { color: var(--olive); }
.outcomes ul { list-style: none; padding-left: 0; margin: 0; }
.outcomes li {
  position: relative; padding-left: 26px; margin-bottom: 7px;
  font: 400 14px/1.6 var(--sans); color: var(--body);
}
.outcomes li:last-child { margin-bottom: 0; }
.outcomes li::before {
  content: ''; position: absolute; left: 0; top: .35em;
  width: 13px; height: 13px; border: 1px solid var(--ink-30); border-radius: 3px;
}

/* the comparison — a table with a lead-in, not a box around a box */
.compare .beatHead { color: var(--olive); }

/* back to the opening — a panel, because it also interrupts */
.revisit {
  padding: 18px 22px;
  background: var(--panel-2);
  border-left: 3px solid var(--olive);
  border-radius: 0 var(--radius) var(--radius) 0;
}
.revisit .beatHead { color: var(--olive-2); }

/* apply — an open prompt; prose with a rule */
.apply { padding-left: 20px; border-left: 2px solid var(--ink-16); }
.apply .beatHead { color: var(--olive-2); }

/* Procedural beats (05-module-structure rule 7b) ---------------------------
   recall — a panel, because like the hook it interrupts: answer before reading.
   Styled as the revisit, not the hook: both reach back, neither is new. */
.recall {
  padding: 18px 22px;
  background: var(--panel-2);
  border-left: 3px solid var(--olive);
  border-radius: 0 var(--radius) var(--radius) 0;
}
.recall .beatHead { color: var(--olive-2); }

/* step — a soft rounded card with a large yellow numeral, so a procedural
   page reads as a short stack of steps (Nick's choice, 2026-09-29, over
   hairlines-and-space and a numbered rail). The card reaches outside the
   reading column rather than into it: its padding sits beyond the measure,
   so the text inside keeps the full line length. */
.step {
  margin-left: -32px; margin-right: -32px;
  padding: 30px 32px 32px; border-radius: 16px;
  background: var(--card); box-shadow: 0 0 0 1px var(--rule);
}
.step + .step { margin-top: 28px; }
.stepHead { display: grid; grid-template-columns: auto 1fr; column-gap: 18px; align-items: end; }
.stepNum { font: 300 64px/.8 var(--serif); color: var(--accent); }
.stepTitle { margin: 0; font: 400 28px/1.2 var(--serif); color: var(--ink); text-wrap: balance; }
.step .beatBody { margin-top: 18px; }
.step .ask { margin-top: 26px; }
@media (max-width: 760px) {
  .step { margin-left: -8px; margin-right: -8px; padding: 22px 18px 24px; }
  .stepNum { font-size: 44px; }
  .stepTitle { font-size: 22px; }
}

/* summary — the one inverted block on the page, and the visual full stop */
.summary {
  padding: 20px 24px; border-radius: var(--radius-lg);
  background: var(--invert-bg); color: var(--invert-fg);
}
.summary .beatHead { color: var(--invert-label); }
.summary ul { list-style: none; padding-left: 0; margin-bottom: 0; color: inherit; }
.summary li { position: relative; padding-left: 18px; margin-bottom: 9px; }
.summary li:last-child { margin-bottom: 0; }
.summary li::before { content: '—'; position: absolute; left: 0; opacity: .5; }
.summary strong { color: inherit; }

/* Answerable items in the hook ------------------------------------------- */
.ask { display: flex; flex-direction: column; gap: 10px; margin: 14px 0 0; }
.askItem {
  display: grid; grid-template-columns: minmax(0, 14rem) 1fr; gap: 10px 16px;
  align-items: center; padding: 10px 12px;
  border: 1px solid var(--ink-12); border-radius: var(--radius);
  background: var(--paper);
  transition: border-color .15s ease, background-color .15s ease;
}
.askRight { border-color: var(--right); background: var(--check-bg); }
.askWrong { border-color: var(--orange); background: var(--wrong-bg); }
.askCode {
  font: 500 13.5px var(--mono); color: var(--ink);
  background: var(--ink-08); padding: 5px 9px; border-radius: var(--radius-sm);
  white-space: pre-wrap; word-break: break-word;
}
.askAnswer { display: flex; gap: 9px; align-items: center; flex-wrap: wrap; }
.askInput {
  flex: 1 1 9rem; min-width: 0; padding: 8px 11px;
  border: 1px solid var(--ink-16); border-radius: var(--radius);
  background: var(--paper); font: 500 13.5px var(--mono); color: var(--ink);
}
.askLines { flex-basis: 100%; resize: vertical; line-height: 1.6; }
.askCheck {
  border: 1px solid var(--ink-30); background: transparent; color: var(--ink);
  border-radius: var(--radius); padding: 8px 14px; cursor: pointer;
  font: 500 12px var(--sans);
}
.askCheck:hover:not(:disabled) { border-color: var(--ink); }
/* A prose question (D-041) takes the whole row; the answer sits under it. */
.askProse { grid-template-columns: 1fr; }
.askQuestion { margin: 0; font: 400 15px/1.55 var(--sans); color: var(--ink); }
/* Choices (D-041): a closed question is buttons, not a text box. */
.askChoices { display: flex; flex-wrap: wrap; gap: 8px; }
.askChoice {
  border: 1px solid var(--ink-30); background: var(--paper); color: var(--ink);
  border-radius: var(--radius); padding: 8px 14px; cursor: pointer;
  font: 500 13.5px var(--mono);
}
.askChoice:hover:not(:disabled) { border-color: var(--ink); }
.askChoice:disabled { cursor: default; opacity: .55; }
.askChoice.askPicked { opacity: 1; border-color: var(--ink); border-width: 2px; }
.askProse .askChoice { font: 500 14px var(--sans); }   /* prose options read as prose */
/* "I don't know" — available, never louder than Check (D-040). */
.askIdk {
  border: none; background: none; padding: 8px 4px; cursor: pointer;
  font: 500 12px var(--sans); color: var(--ink-55); text-decoration: underline;
}
.askIdk:hover { color: var(--ink); }
.askCheck:disabled { opacity: .45; cursor: default; }
 /* On a phone the code needs the full width — beside the answer box it broke
   mid-token ("Boolea / n(\"0\")"). */
@media (max-width: 560px) {
  .askItem { grid-template-columns: 1fr; }
}
.askVerdict { font: 600 10.5px var(--mono); letter-spacing: .14em; text-transform: uppercase; }
.askVRight { color: var(--right); }
.askVWrong { color: var(--orange); }
.askWhy { grid-column: 1 / -1; margin: 0; font: 400 12.5px/1.6 var(--sans); color: var(--ink-68); }
/* Shown only on a miss, above the rule: name what they probably thought first. */
.askMiss {
  grid-column: 1 / -1; margin: 0 0 2px;
  font: 400 12.5px/1.6 var(--sans); color: var(--ink);
  padding-left: 10px; border-left: 2px solid var(--orange);
}
.askMiss strong { font-weight: 700; }
.askScore { margin: 6px 0 0; font: 400 13px/1.6 var(--sans); color: var(--ink-68); }
.askScore strong { color: var(--ink); }

/* Folded scaffolding ------------------------------------------------------ */
.scaffold { margin: 0; }
.scaffoldFolded {
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  width: 100%; margin: 26px 0; padding: 14px 16px;
  border: 1px dashed var(--ink-16); border-radius: var(--radius-lg);
  background: transparent; cursor: pointer; text-align: left;
  font: 400 13px var(--sans); color: var(--ink-55);
}
.scaffoldFolded:hover { border-color: var(--olive); color: var(--ink); }
.scaffoldKicker {
  font: 600 10px var(--mono); letter-spacing: .14em; text-transform: uppercase;
  color: var(--olive); padding: 3px 7px; background: var(--ink-08); border-radius: 4px;
}

/* :::repo — the panel keeps its own look; this only spaces it in the prose */
.repo { margin: 22px 0; }

/* :::indexes — an array or string and its indexes (Nick chose the table,
   2026-10-25). Light cells, dark ink; only the indexes are coloured. */
.indexes { margin: 22px 0; }
.indexesRow { display: flex; align-items: center; min-width: 0; }
.indexesVar {
  flex: none; font: 400 15px var(--mono); color: var(--ink);
  padding: 9px 12px; border: 1.5px solid var(--ink-30); border-radius: var(--radius-lg);
  background: var(--panel-2);
}
.indexesArrow { flex: 0 0 26px; height: 12px; position: relative; }
.indexesArrow::before { content: ''; position: absolute; left: 3px; right: 7px; top: 5px; border-top: 2px solid var(--ink-45); }
.indexesArrow::after {
  content: ''; position: absolute; right: 2px; top: 1px;
  border-left: 7px solid var(--ink-45); border-top: 5px solid transparent; border-bottom: 5px solid transparent;
}
.indexesScroll { overflow-x: auto; min-width: 0; }
.indexesTable { border-collapse: collapse; font: 400 16px var(--mono); }
.indexesTable th {
  font: 400 12px var(--sans); color: var(--ink-55);
  text-align: left; padding: 8px 10px; white-space: nowrap;
  border: 1px solid var(--rule);
}
.indexesTable tr:first-child th { background: var(--panel-2); }
.indexesTable td {
  min-width: 44px; box-sizing: border-box; text-align: center; padding: 9px 10px;
  color: var(--ink); border: 1px solid var(--rule); background: var(--card); white-space: nowrap;
}
.indexesTable tr:first-child td { background: var(--panel-2); }
.indexesTable td.indexesIdx { color: var(--slate); font-weight: 600; font-size: 14px; }
.indexesCaption { margin-top: 8px; font: 400 14px var(--sans); color: var(--ink-72); }
@media (max-width: 480px) {
  .indexesTable td { min-width: 34px; padding: 8px 6px; font-size: 14px; }
  .indexesVar { font-size: 13px; padding: 7px 8px; }
}

/* :::coercion — each side converts toward the operator (Nick's design,
   2026-10-26). Values in light chips; the pair finally compared is darker;
   step numbers in slate over the arrows. */
.coerce { margin: 22px 0; font-variant-ligatures: none; }
.coerce code { font-variant-ligatures: none; font-feature-settings: "calt" 0, "liga" 0; }
.coerceRow { display: flex; align-items: flex-end; flex-wrap: wrap; gap: 6px 4px; }
.coerceValue {
  font: 400 16px var(--mono); color: var(--ink-72); white-space: nowrap;
  padding: 7px 10px; border: 1px solid var(--rule); border-radius: var(--radius);
  background: var(--card);
}
.coerceFinal { color: var(--ink); border-color: var(--ink-45); font-weight: 600; }
.coerceArrow {
  display: inline-flex; flex-direction: column; align-items: center;
  font: 400 18px var(--sans); color: var(--ink-45); padding: 0 2px; line-height: 1;
  margin-bottom: 8px;
}
.coerceStep { font: 600 12px var(--mono); color: var(--slate); margin-bottom: 3px; }
.coerceOp {
  font: 600 18px var(--mono); color: var(--ink); padding: 0 10px; margin-bottom: 8px;
  font-feature-settings: "calt" 0, "liga" 0; letter-spacing: .08em;
}
@media (max-width: 480px) {
  .coerceValue { font-size: 14px; padding: 6px 7px; }
  .coerceOp { padding: 0 6px; }
}
